import React, { useState } from 'react';
import { i18n } from '../../i18n/i18n';
import { useAppContext } from '../../context/AppContext';
import { setStatsVisibilityApi } from '../../services/apiService';
import { showToast } from '../kid/Toast';
import type { StatsVisibility } from '../../shared/types/stats';

/** Admin setting: what children see on the Stats Family tab (docs/design/family-stats.md, D1). */
const OPTIONS: StatsVisibility[] = ['team', 'full', 'admins'];

const StatsVisibilitySetting: React.FC = () => {
  const { current_family, refreshFamilyData } = useAppContext() as any;
  const current: StatsVisibility = current_family?.stats_visibility || 'team';
  const [saving, setSaving] = useState<StatsVisibility | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const choose = async (v: StatsVisibility) => {
    if (v === current || saving) return;
    setSaving(v);
    setErr(null);
    try {
      await setStatsVisibilityApi(v);
      await refreshFamilyData();
      showToast({ message: i18n.t('stats.visibility_saved') });
    } catch (e: any) {
      setErr(e?.message || 'Error');
    } finally {
      setSaving(null);
    }
  };

  return (
    <section className="space-y-2">
      <h2 className="font-kid-heading font-extrabold text-xl px-1">{i18n.t('stats.visibility_title')}</h2>
      <p className="text-base px-1">{i18n.t('stats.visibility_help')}</p>
      <div
        className="rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark divide-y-2 divide-kid-border dark:divide-kid-border-dark overflow-hidden"
        role="radiogroup"
        aria-label={i18n.t('stats.visibility_title')}
      >
        {OPTIONS.map((v) => (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={current === v}
            disabled={!!saving}
            onClick={() => choose(v)}
            className="w-full min-h-[60px] px-4 py-3 flex items-center gap-3 text-left font-bold text-lg"
          >
            <span
              aria-hidden
              className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center ${current === v ? 'border-kid-green' : 'border-kid-border-strong dark:border-kid-border-strong-dark'}`}
            >
              {current === v && <span className="w-3 h-3 rounded-full bg-kid-green" />}
            </span>
            <span className="flex-1">{i18n.t(`stats.visibility_${v}`)}</span>
            {saving === v && <span aria-hidden>…</span>}
          </button>
        ))}
      </div>
      {err && <p role="alert" className="font-bold text-red-700 dark:text-red-300 px-1">{err}</p>}
    </section>
  );
};

export default StatsVisibilitySetting;
